import { useContext, useState } from "react";
import * as XLSX from "xlsx";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import Table from "../../componentsTable/Table";
import { petitions } from "../../../api/calls";
import { DataContext } from "../../../context/DataContext";
import { NavLink } from "react-router-dom";

export default function ImportProductos() {
  const { session, idEmpresa } = useContext(DataContext);
  const [showInstructions, setShowInstructions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [file, setFile] = useState(null);
  const [productos, setProductos] = useState([]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      multiple: false,

      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
          ".xls",
        ],
        "application/vnd.ms-excel": [".xls"],
      },

      onDrop: async (acceptedFiles) => {
        if (!acceptedFiles.length) return;

        setProductos([]);
        setFile(null);
        setLoadingText("Validando plantilla...");
        setLoading(true);

        const ok = await validarPlantilla(acceptedFiles[0]);

        setLoading(false);

        if (!ok) {
          return;
        }

        setFile(acceptedFiles[0]);
      },
    });
  const handleRemove = () => {
    setFile(null);
    setProductos([]);
  };

  const validarPlantilla = async (archivo) => {
    setLoading(true);

    setFile(null);

    try {
      const buffer = await archivo.arrayBuffer();

      const workbook = XLSX.read(buffer);

      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const filas = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        blankrows: false,
      });

      if (!filas.length) {
        toast.error("La plantilla está vacía.");
        return false;
      }

      const cabeceras = filas[0].map((x) => String(x).trim().toUpperCase());

      const esperadas = [
        "TIPO",
        "NOMBRE",
        "PRECIO COMPRA",
        "PRECIO VENTA",
        "STOCK",
      ];

      const coincide =
        cabeceras.length >= esperadas.length &&
        esperadas.every((c, i) => cabeceras[i] === c);

      if (!coincide) {
        toast.error("El archivo no corresponde a la plantilla.");
        return false;
      }

      setFile(archivo);

      setLoadingText("Leyendo registros del archivo...");
      await new Promise((resolve) => setTimeout(resolve, 50));

      const rows = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
      });

      const lista = rows.map((x) => ({
        tipo: x["TIPO"],
        nombre: x["NOMBRE"],
        pcompra: x["PRECIO COMPRA"],
        pventa: x["PRECIO VENTA"],
        stock: x["STOCK"],
      }));

      setProductos(lista);
      setFile(archivo);

      return true;
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      accessorKey: "tipo",
      header: "TIPO",
      className: "white-space-nowrap",
      cell: ({ getValue }) => (
        <td className="white-space-nowrap">{getValue()}</td>
      ),
    },
    {
      accessorKey: "nombre",
      header: "NOMBRE",
      cell: ({ getValue }) => {
        const texto = getValue() ?? "";

        return (
          <td style={{ maxWidth: 320 }}>
            <div
              style={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {texto}
            </div>
          </td>
        );
      },
    },
    {
      accessorKey: "pcompra",
      header: "PRECIO COMPRA",
      cell: ({ getValue }) => <td>{getValue()}</td>,
    },
    {
      accessorKey: "pventa",
      header: "PRECIO VENTA",
      cell: ({ getValue }) => <td>{getValue()}</td>,
    },
    {
      accessorKey: "stock",
      header: "STOCK",
      cell: ({ getValue }) => <td>{getValue()}</td>,
    },
  ];

  const handleRegistrar = async () => {
    if (!file) {
      toast.error("Debe seleccionar un archivo.");
      return;
    }

    try {
      setLoadingText("Enviando archivo al servidor...");
      setLoading(true);

      const response = await petitions.importProductos(
        idEmpresa(),
        session().token,
        file,
      );

      if (response.status === "success") {
        toast.success(
          response.message || "Productos importados correctamente.",
        );

        handleRemove();
      } else {
        const msg =
          typeof response.code === "string" ? response.message : response.data;

        toast.error(msg);
      }
    } catch (error) {
      toast.error("Ocurrió un error al importar el archivo.");
      console.error(error);
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  const handleDownloadTemplate = async () => {
    const response = await petitions.getPlantillaImportacion(
      session().token,
      "P",
    );

    if (response.isError) {
      toast.error(response.message);
      return;
    }

    const url = window.URL.createObjectURL(response.blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "PlantillaProducto.xlsx";

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <div>
        <NavLink
          className="btn btn-phoenix-secondary rounded-4"
          to={"/main/VerProducto"}
        >
          <span className="fas fa-arrow-left me-2"></span>
          Volver
        </NavLink>
      </div>

      <div className="text-center mt-4">
        <h3 className="fw-bold mb-2 mb-6">
          Importar productos desde un archivo Excel
        </h3>

        {productos.length === 0 && (
          <div
            className="dropzone dropzone-multiple p-0 mx-auto"
            style={{ maxWidth: 620 }}
            id="dropzone"
          >
            <div
              {...getRootProps()}
              className={`dz-message rounded-4 ${
                isDragActive ? "bg-primary text-white" : ""
              } ${isDragReject ? "text-danger" : ""}`}
              data-dz-message="data-dz-message"
            >
              <input {...getInputProps()} name="file" />
              <div className="dz-message-text">
                <div className="text-center py-5">
                  <span
                    className="far fa-file-excel text-success mb-4"
                    style={{ fontSize: 64 }}
                  />

                  <h4 className="fw-semibold mt-3">Selecciona tu archivo</h4>

                  <p className="text-body-secondary mb-4">
                    Arrastra un archivo Excel aquí o haz clic para
                    seleccionarlo.
                  </p>

                  <button
                    type="button"
                    className="btn btn-primary rounded-4 px-5"
                  >
                    <span className="fas fa-cloud-upload-alt me-2" />
                    Subir archivo Excel
                  </button>

                  <div className="small text-body-secondary mt-4">
                    Formatos permitidos: .xlsx y .xls
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="dz-preview dz-preview-multiple m-0 d-flex flex-column">
                <div className="d-flex flex-column align-items-center justify-content-center py-6">
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                  />

                  <h5 className="fw-semibold mb-1">{loadingText}</h5>

                  <small className="text-body-secondary">
                    Espere un momento.
                  </small>
                </div>
              </div>
            ) : file ? (
              <div className="dz-preview dz-preview-multiple m-0 d-flex flex-column">
                <div className="d-flex pb-3 border-bottom border-translucent media px-2">
                  <div className="border p-2 rounded-2 me-2">
                    <div
                      className="rounded-3 bg-success-subtle border d-flex justify-content-center align-items-center"
                      style={{
                        width: 80,
                        height: 80,
                      }}
                    >
                      <span
                        className="far fa-file-excel text-success"
                        style={{
                          fontSize: 42,
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex-1 d-flex flex-between-center">
                    <div>
                      <h5 className="mb-1">{file.name}</h5>

                      <div className="small text-body-secondary">
                        {(file.size / 1024).toFixed(1)} KB
                      </div>

                      <div className="mt-2">
                        <span className="badge badge-phoenix badge-phoenix-success">
                          Listo para importar
                        </span>
                      </div>
                    </div>

                    <button
                      className="btn btn-phoenix-danger btn-sm"
                      onClick={handleRemove}
                    >
                      <span className="fas fa-trash-alt me-2" />
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {productos.length > 0 && (
          <div className="mt-5 mx-auto" style={{ maxWidth: 620 }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="fw-semibold mb-1">Vista previa</h5>

                <small className="text-body-secondary">
                  {productos.length} registros encontrados
                </small>
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-phoenix-secondary rounded-4"
                  onClick={handleRemove}
                >
                  <span className="fas fa-broom me-2"></span>
                  Limpiar
                </button>

                <button
                  className="btn btn-primary rounded-4"
                  onClick={handleRegistrar}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <span className="fas fa-check me-2"></span>
                      Registrar
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="text-center mb-4">
              <h4 className="fw-semibold mb-2">
                ¿Te parece correcta la información?
              </h4>

              <p className="text-body-secondary mb-0">
                Revisa los registros antes de importarlos.
              </p>
            </div>

            <Table
              data={productos}
              columns={columns}
              filtering=""
              setFiltering={() => {}}
              handleRowClick={() => {}}
              classNameTable="table table-sm fs-9 mb-0"
            />
          </div>
        )}

        <div className="text-center mt-4">
          <button className="btn btn-outline-success rounded-4" onClick={() => handleDownloadTemplate()}>
            <span className="fas fa-download me-2"></span>
            Descargar plantilla
          </button>
        </div>

        <div className="text-center mt-4">
          <button
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => setShowInstructions(!showInstructions)}
          >
            <span className="far fa-question-circle me-2"></span>
            ¿Necesitas ayuda para crear tu archivo Excel?
            <span className="text-primary fw-semibold ms-2">
              Ver instrucciones
            </span>
            <span
              className={`fas ${
                showInstructions ? "fa-chevron-up" : "fa-chevron-down"
              } ms-2`}
            />
          </button>
        </div>

        {showInstructions && (
          <div
            className="card border-0 rounded-4 p-4 mt-3 text-start mx-auto shadow-sm"
            style={{
              maxWidth: 600,
            }}
          >
            <div className="fw-semibold mb-4">
              <span className="fas fa-info-circle text-primary me-2"></span>
              Recuerda: Todas las cabeceras son sensibles a mayúsculas.
            </div>

            <table className="table table-borderless align-middle mb-0">
              <tbody>
                <tr>
                  <td width="220" className="fw-bold">
                    TIPO
                  </td>

                  <td>
                    <span className="badge badge-phoenix fs-10 badge-phoenix-danger rounded-4">
                      Requerido
                    </span>

                    <div className="mt-2">
                      Solo puede tomar uno de estos valores:
                    </div>

                    <ul className="mb-0 mt-2">
                      <li>P → Producto</li>
                      <li>S → Servicio</li>
                    </ul>
                  </td>
                </tr>

                <tr>
                  <td className="fw-bold">NOMBRE</td>

                  <td>
                    <span className="badge badge-phoenix fs-10 badge-phoenix-danger rounded-4">
                      Requerido
                    </span>

                    <div className="mt-2">Nombre del producto o servicio.</div>
                  </td>
                </tr>

                <tr>
                  <td className="fw-bold">PRECIO COMPRA</td>

                  <td>
                    <span className="badge badge-phoenix fs-10 badge-phoenix-danger rounded-4">
                      Requerido
                    </span>

                    <div className="mt-2">Debe ser un valor numérico.</div>

                    <div>Ejemplo:</div>

                    <ul className="mb-0 mt-2">
                      <li>0</li>
                      <li>10</li>
                      <li>15.50</li>
                    </ul>
                  </td>
                </tr>

                <tr>
                  <td className="fw-bold">PRECIO VENTA</td>

                  <td>
                    <span className="badge badge-phoenix fs-10 badge-phoenix-danger rounded-4">
                      Requerido
                    </span>

                    <div className="mt-2">Debe ser un valor numérico.</div>
                  </td>
                </tr>

                <tr>
                  <td className="fw-bold">STOCK</td>

                  <td>
                    <span className="badge badge-phoenix fs-10 badge-phoenix-danger rounded-4">
                      Requerido
                    </span>

                    <div className="mt-2">Debe ser un valor numérico.</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
